import { useTranslation } from 'react-i18next';
import { Box, Stack, Typography } from '@mui/material';
import TuneIcon from '@mui/icons-material/Tune';

import { useFilterForm } from '../../hooks/useFilterForm';
import { useFilterData } from '../../hooks/useFilterData';
import { FilterAutocomplete } from './FilterAutocomplete';
import { filterContainerStyles, filterHeaderStyles } from './styles';

interface CaseFiltersFormProps {
  agencyId: number;
}

export const CaseFiltersForm = ({ agencyId }: CaseFiltersFormProps) => {
  const { t } = useTranslation();

  const { selectedTeams, selectedWorkflows, selectedSteps, selectedAgents, selectedClients, handleFilterChange } =
    useFilterForm();

  const workflowsIds = selectedWorkflows?.map((id: string) => id) ?? [];

  const { agentList, teamsList, workflowsList, stepsList, clientsList } = useFilterData(agencyId, workflowsIds);

  const teamsValue = teamsList?.items.filter((team) => selectedTeams?.includes(team.id));
  const workflowValue = workflowsList?.items.filter((workflow) => selectedWorkflows?.includes(workflow.id));
  const stepsValue = stepsList?.filter((step) => selectedSteps?.includes(step.id));
  const clientsValue = clientsList?.items
    .filter((client) => selectedClients?.includes(client.id))
    .map((client) => ({
      ...client,
      name: `${client.firstName} ${client.lastName}`,
    }));

  const agentsWithNameAndColor = agentList?.items.map((agent) => ({
    ...agent,
    name: `${agent.firstName} ${agent.lastName}`,
    color: '',
  }));

  const agentsValue = agentsWithNameAndColor?.filter((agent) => selectedAgents?.includes(agent.id));

  return (
    <Box sx={filterContainerStyles}>
      <Stack sx={filterHeaderStyles}>
        <TuneIcon />
        <Typography variant="subtitle1">{t('calls.assignment.filters.title')}</Typography>
      </Stack>

      <Stack gap={2}>
        <FilterAutocomplete
          value={teamsValue}
          options={teamsList?.items}
          onChange={handleFilterChange('teams')}
          getOptionLabel={(option) => option.name}
          placeholder={t('calls.assignment.filters.team')}
        />

        <FilterAutocomplete
          value={clientsValue}
          options={clientsList?.items.map((client) => ({
            ...client,
            name: `${client.firstName} ${client.lastName}`,
          }))}
          onChange={handleFilterChange('clients')}
          getOptionLabel={(option) => option.name}
          placeholder={t('calls.assignment.filters.client')}
        />

        <FilterAutocomplete
          value={workflowValue}
          options={workflowsList?.items}
          onChange={handleFilterChange('workflows')}
          getOptionLabel={(option) => option.name}
          placeholder={t('calls.assignment.filters.workflow')}
        />

        <FilterAutocomplete
          value={stepsValue}
          options={stepsList}
          onChange={handleFilterChange('steps')}
          getOptionLabel={(option) => option.name}
          placeholder={t('calls.assignment.filters.step')}
          disabled={!selectedWorkflows?.length}
        />

        <FilterAutocomplete
          value={agentsValue}
          options={agentsWithNameAndColor}
          onChange={handleFilterChange('agents')}
          getOptionLabel={(option) => option.name}
          placeholder={t('calls.assignment.filters.agent')}
        />
      </Stack>
    </Box>
  );
};
